import { useEffect, useMemo, useState } from 'react'
import { Package, ClipboardList, TrendingUp } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'

// Read-only "what have we ordered" overview for a single site.
// Aggregates the site's own order_lines into totals per product.
function fmtMoney(n) {
  return '£' + Number(n || 0).toFixed(2)
}

export default function Activity({ activeCompanyId }) {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  useEffect(() => {
    if (isDemoMode) return
    setLoading(true)
    // No activeCompanyId = "All sites" mode. RLS still limits to the user's
    // linked companies, so we get an aggregate across their whole group.
    let q = supabase
      .from('orders')
      .select('id, reference, status, placed_at, created_at, company_id, companies(id, name), order_lines(qty, price_at_order, name_at_order, sku_at_order, unit_at_order, pack_size_at_order)')
      .order('created_at', { ascending: false })
    if (activeCompanyId) q = q.eq('company_id', activeCompanyId)
    if (from) q = q.gte('created_at', `${from}T00:00:00Z`)
    if (to)   q = q.lte('created_at', `${to}T23:59:59Z`)
    q.then(({ data }) => {
      setOrders(data || [])
      setLoading(false)
    })
  }, [activeCompanyId, from, to])

  const summary = useMemo(() => {
    let totalValue = 0
    const byProduct = new Map()
    for (const o of orders) {
      for (const l of (o.order_lines || [])) {
        const value = Number(l.price_at_order || 0) * Number(l.qty || 0)
        totalValue += value
        const key = l.name_at_order || l.sku_at_order || '(unknown)'
        const prev = byProduct.get(key) || {
          name: l.name_at_order, sku: l.sku_at_order, unit: l.unit_at_order, pack: l.pack_size_at_order,
          qty: 0, value: 0, orderIds: new Set(), lastOrdered: null,
        }
        prev.qty += Number(l.qty || 0)
        prev.value += value
        prev.orderIds.add(o.id)
        const placed = o.placed_at || o.created_at
        if (!prev.lastOrdered || new Date(placed) > new Date(prev.lastOrdered)) prev.lastOrdered = placed
        byProduct.set(key, prev)
      }
    }
    const rows = Array.from(byProduct.values())
      .map(r => ({ ...r, orderCount: r.orderIds.size }))
      .sort((a, b) => b.value - a.value)
    return { totalValue, totalOrders: orders.length, rows }
  }, [orders])

  const isAllSites = !activeCompanyId

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-brand" />
          Activity {isAllSites && <span className="text-sm font-normal text-gray-500">· All sites combined</span>}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {isAllSites
            ? 'Totals for every site you can see, rolled up into one view. Switch to a single site from the sidebar to focus on just that one.'
            : 'Totals for everything this site has ordered. Use the date filters to narrow it down.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
          <label>
            <span className="text-xs text-gray-500 block mb-1">From</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">To</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="input-field" />
          </label>
        </div>
      </div>

      {loading && <div className="card text-sm text-gray-500">Loading…</div>}

      {!loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="card">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Orders placed</div>
              <div className="text-2xl font-semibold text-gray-800 mt-1 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-brand" /> {summary.totalOrders}
              </div>
            </div>
            <div className="card">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Total value</div>
              <div className="text-2xl font-semibold text-gray-800 mt-1">{fmtMoney(summary.totalValue)}</div>
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Package className="w-5 h-5 text-brand" /> By product
            </h3>
            {summary.rows.length === 0
              ? <p className="text-sm text-gray-500">No orders in the selected period yet.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase text-gray-500 border-b border-gray-200">
                        <th className="text-left py-2">Product</th>
                        <th className="text-right py-2">Times ordered</th>
                        <th className="text-right py-2">Total qty</th>
                        <th className="text-right py-2">Total value</th>
                        <th className="text-right py-2">Last ordered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.rows.map(r => (
                        <tr key={r.name + (r.sku || '')} className="border-b border-gray-50">
                          <td className="py-2">
                            <div className="font-medium text-gray-800">{r.name}</div>
                            {r.sku && <div className="text-xs text-gray-500">{r.sku}{r.pack ? ` · ${r.pack}/box` : ''}</div>}
                          </td>
                          <td className="py-2 text-right">{r.orderCount}</td>
                          <td className="py-2 text-right">{r.qty}{r.unit ? ` ${r.unit}` : ''}</td>
                          <td className="py-2 text-right font-medium">{fmtMoney(r.value)}</td>
                          <td className="py-2 text-right text-gray-500 text-xs">
                            {r.lastOrdered ? new Date(r.lastOrdered).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </>
      )}
    </div>
  )
}
